import type { TasApplicationState } from 'tio-common/types/tuition-assistance';
import type { ChipSignature } from 'tio-ui/components/chip';

// Valid Chip intents from tio-ui
export type ChipIntent = 'primary' | 'success' | 'warning' | 'danger' | 'default';

// Icon types for status indicator
export type StatusIconType = 'clock' | 'warning' | 'check' | 'x';

export interface StatusInfo {
  label: string;
  intent: ChipIntent;
  icon: StatusIconType;
}

/**
 * Consolidated status display mapping for TAS Application states
 * Used by dashboard components for consistent status display
 * Labels match tasStateLabelMap from tas-application.ts model
 */
export const APPLICATION_STATUS_MAP: Record<string, StatusInfo> = {
  'TAS.ApplicationState.CREATED': { label: 'Created', intent: 'primary', icon: 'clock' },
  'TAS.ApplicationState.DEFINE_COURSES': { label: 'Draft', intent: 'primary', icon: 'clock' },
  'TAS.ApplicationState.ATTEND': { label: 'In Progress', intent: 'primary', icon: 'clock' },
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': {
    label: 'Pending Courses Approval',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ApplicationState.COURSES_APPROVED': {
    label: 'Approved',
    intent: 'success',
    icon: 'clock',
  },
  'TAS.ApplicationState.COURSES_NOT_APPROVED': {
    label: 'Not Approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.COURSES_REJECTED': {
    label: 'Rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.PENDING_EVIDENCE_SUBMISSION': {
    label: 'Awaiting Evidence',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': {
    label: 'Pending Evidence Approval',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ApplicationState.EVIDENCE_APPROVED': {
    label: 'Evidence Approved',
    intent: 'success',
    icon: 'clock',
  },
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED': {
    label: 'Evidence Not Approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.EVIDENCE_REJECTED': {
    label: 'Evidence Rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.REJECTED_PER_POLICY': {
    label: 'Rejected Per Policy',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.PENDING_PAYMENT': {
    label: 'Ready for Payment',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ApplicationState.FULFILLED': { label: 'Complete', intent: 'success', icon: 'check' },
  'TAS.ApplicationState.ABANDONED': { label: 'Abandoned', intent: 'default', icon: 'warning' },
};

/**
 * Consolidated status display mapping for TAS Program Instance states
 * Used for Type 4 program instances
 */
export const PROGRAM_INSTANCE_STATUS_MAP: Record<string, StatusInfo> = {
  'TAS.ProgramInstanceState.CREATED': { label: 'Draft', intent: 'primary', icon: 'clock' },
  'TAS.ProgramInstanceState.DRAFT': { label: 'Draft', intent: 'primary', icon: 'clock' },
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
    label: 'Pending Program Approval',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ProgramInstanceState.PROGRAM_APPROVED': {
    label: 'Approved',
    intent: 'success',
    icon: 'check',
  },
  'TAS.ProgramInstanceState.PROGRAM_NOT_APPROVED': {
    label: 'Not Approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.PROGRAM_REJECTED': {
    label: 'Rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.PRIMED': { label: 'In Progress', intent: 'primary', icon: 'clock' },
  'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': {
    label: 'Pending Completion Approval',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ProgramInstanceState.COMPLETION_APPROVED': {
    label: 'Completed',
    intent: 'success',
    icon: 'check',
  },
  'TAS.ProgramInstanceState.COMPLETION_NOT_APPROVED': {
    label: 'Not Approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.COMPLETION_REJECTED': {
    label: 'Rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': {
    label: 'Withdrawal Pending',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ProgramInstanceState.WITHDRAWAL_APPROVED': {
    label: 'Withdrawn',
    intent: 'default',
    icon: 'check',
  },
  'TAS.ProgramInstanceState.WITHDRAWAL_NOT_APPROVED': {
    label: 'Withdrawal Not Approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED': {
    label: 'Withdrawal Rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.COMPLETED': { label: 'Completed', intent: 'success', icon: 'check' },
  'TAS.ProgramInstanceState.WITHDRAWN': { label: 'Withdrawn', intent: 'default', icon: 'warning' },
  'TAS.ProgramInstanceState.ABANDONED': { label: 'Abandoned', intent: 'default', icon: 'warning' },
};

const DEFAULT_STATUS: StatusInfo = { label: 'Unknown', intent: 'primary', icon: 'clock' };

/**
 * Get status info for a given application or program instance state
 * Checks both APPLICATION_STATUS_MAP and PROGRAM_INSTANCE_STATUS_MAP
 */
export function getApplicationStatusInfo(state: string | undefined): StatusInfo {
  if (!state) return DEFAULT_STATUS;
  return APPLICATION_STATUS_MAP[state] || PROGRAM_INSTANCE_STATUS_MAP[state] || DEFAULT_STATUS;
}

const tasApplicationStateBadgeMap: Record<
  string,
  { intent: ChipSignature['Args']['intent']; label: string }
> = {
  'TAS.ApplicationState.CREATED': { intent: 'success', label: 'Draft' },
  'TAS.ApplicationState.DEFINE_COURSES': { intent: 'success', label: 'Draft' },
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': { intent: 'primary', label: 'Pending' },
  'TAS.ApplicationState.COURSES_APPROVED': { intent: 'success', label: 'In Progress' },
  'TAS.ApplicationState.COURSES_NOT_APPROVED': { intent: 'danger', label: 'Rejected' },
  'TAS.ApplicationState.COURSES_REJECTED': { intent: 'danger', label: 'Rejected' },
  'TAS.ApplicationState.ATTEND': { intent: 'success', label: 'In Progress' },
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': { intent: 'primary', label: 'Pending' },
  'TAS.ApplicationState.EVIDENCE_APPROVED': { intent: 'success', label: 'In Progress' },
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED': { intent: 'danger', label: 'Rejected' },
  'TAS.ApplicationState.EVIDENCE_REJECTED': { intent: 'danger', label: 'Rejected' },
  'TAS.ApplicationState.FULFILLED': { intent: 'success', label: 'Completed' },
  'TAS.ApplicationState.ABANDONED': { intent: 'warning', label: 'Abandoned' },
};

export const applicationBadgeState = (state: TasApplicationState, approvalRequired: boolean) => {
  if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL' && !approvalRequired) {
    // if 4.2 or 4.3
    return tasApplicationStateBadgeMap['TAS.ApplicationState.COURSES_APPROVED'];
  }
  return tasApplicationStateBadgeMap[state];
};

const tasApplicationStateCourseBadgeMap: Record<
  string,
  { intent: ChipSignature['Args']['intent']; label: string }
> = {
  'TAS.ApplicationState.CREATED': { intent: 'warning', label: 'Add Courses' },
  'TAS.ApplicationState.DEFINE_COURSES': { intent: 'warning', label: 'Add Courses' },
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': { intent: 'primary', label: 'Pending' },
  'TAS.ApplicationState.COURSES_APPROVED': { intent: 'success', label: 'Approved' },
  'TAS.ApplicationState.COURSES_NOT_APPROVED': { intent: 'danger', label: 'Rejected' },
  'TAS.ApplicationState.COURSES_REJECTED': { intent: 'danger', label: 'Rejected' },
  'TAS.ApplicationState.ATTEND': { intent: 'success', label: 'In Progress' },
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': { intent: 'primary', label: 'Pending' },
  'TAS.ApplicationState.EVIDENCE_APPROVED': { intent: 'success', label: 'In Progress' },
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED': { intent: 'danger', label: 'Rejected' },
  'TAS.ApplicationState.EVIDENCE_REJECTED': { intent: 'danger', label: 'Rejected' },
  'TAS.ApplicationState.FULFILLED': { intent: 'success', label: 'Completed' },
  'TAS.ApplicationState.ABANDONED': { intent: 'warning', label: 'Abandoned' },
};

export const courseBadgeState = (state: TasApplicationState) => {
  return tasApplicationStateCourseBadgeMap[state];
};
